import { FormStatus } from '../../../enum/form-status.enum';
import { DialogReturnData } from '../../../models/dialog-return-data.model';
import { DataAction } from '../../../enum/data-action.enum';
import { MenuItemId } from '../../../enum/menu-item-id.enum';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StorageService } from '../../../services/storage.service';
import { DialogConfig } from '../../../models/dialog-config.model';
import { FilterService, MenuItem, TableState } from 'primeng/api';
import { Table } from 'primeng/table';
import { TableColumn } from '../../../models/table-column.model';
import { TableSelectionMode } from '../../../enum/table-selection-mode.enum';
import { BaseFormComponent } from './base-form.component';
import { Subject, takeUntil } from 'rxjs';
import { Component, inject, AfterViewInit, ElementRef, ViewChild, Renderer2 } from '@angular/core';
import { TabMenu } from 'primeng/tabmenu';
import { DomHandler } from 'primeng/dom';


/**
 * 雙檔表單的抽象類別，所謂的雙檔表單就是除了上方有輸入的表單外，下方還有 Table 呈現的畫面。
 * 繼承基本表單 BaseFormComponent，除了原有單檔做的事情外，這裡負責處理 Table 相關控制。
 * 如果不是資料維護的功能，像是查詢，也算是這種類型，只要繼承後設定不需要 Menubar 就可以。
 *
 * @export
 * @abstract
 * @class BaseFormTableComponent
 * @extends {BaseFormComponent}
 * @implements {AfterViewInit}
 */
@Component({
  template: '',
})
export abstract class BaseFormTableComponent extends BaseFormComponent implements AfterViewInit {

  // ------------ 以下開始為單身 Table 相關 properties -------------

  /**
   * 單身的狀態。
   * 通常用在控制功能選單按鈕狀態。
   * FormStatus.VIEW: 瀏覽 / FormStatus.EDIT: 編輯
   * 預設都是瀏覽狀態，如果是查詢或是表單的功能再 override 覆寫成 FormStatus.EDIT。
   */
  detailFormStatus: FormStatus = FormStatus.VIEW;

  /**
   * 下方 Table 的資料。
   * 如果希望寫程式可以點出提示，可以 override 這個自行定義資料型別。
   */
  detailDatas: any[] = [];

  /**
   * Table 是單選: TableSelectionMode.SINGLE 或多選: TableSelectionMode.MULTIPLE。
   * 預設是單選: TableSelectionMode.SINGLE。
   */
  tableSelectionMode: 'single' | 'multiple' = TableSelectionMode.SINGLE;

  /**
   * Table 的欄位設定。
   */
  tableColumns: TableColumn[] = [];

  /**
   * Table 顯示的欄位設定。
   * 為了 Column Toggle 功能使用，或是 table 有用頁籤拆分欄位也會用到。
   * 如果你沒有 Column Toggle，也沒有頁籤，就把 selectedColumns = tableColumns 就好。
   */
  selectedColumns: TableColumn[] = [];

  /**
   * 畫面上的 Table 物件。
   *
   * 要用 @ViewChild('Table 上 #的名稱') 去畫面取得物件。
   */
  @ViewChild('tableComponent') tableComponent!: Table;

  /**
   * 畫面上的 Table 的 ElementRef。
   * 如果有分頁籤或 inline edit 控制 focus 會用到。
   * ElementRef 是 Angular 框架中的一個類別，它是用於存取與操作 DOM 元素的包裹器。
   *
   * 要用 @ViewChild('Table 上 #的名稱', { read: ElementRef }) 去畫面取得 ElementRef。
   */
  @ViewChild('tableComponent', { read: ElementRef }) tableComponentRef!: ElementRef;

  /**
   * Table 資料的 key 欄位名稱。
   */
  abstract rowDataKeyName: string;

  /**
   * Table 分頁排序狀態保存到 sessionStorage 的名稱。
   * ngOnInit() 的時候會設成 componentClassName + 'TableState'。
   */
  tableStateStorageName: string = '';

  /**
   * Table 上方的功能選單。
   */
  detailMenu: MenuItem[] = [];

  /**
   * 用在記錄目前 Table 選取了哪些資料。
   * 如果是單選，會直接放那筆的資料物件。
   * 如果是多選，會是那筆資料物件的陣列。
   */
  rowSelectedDatas: any;

  /**
   * Table 是否有選取資料
   */
  isRowSelected: boolean = false;

  /**
   * Table 是否要顯示批次刪除的按鈕。
   * 為了避免影響既有程式，預設不提供批次刪除
   * 有需要的再自己 override。
   *
   * 注意；如果要有批次刪除，記得自己 override doDetailMenuBatchDelete()
   * 如果有需要做特別的運算處理要寫在這裡。
   * 為了避免影響既有程式，這個在 simple 類別已經先 override 並且不做任何處理了。
   */
  canBatchDeleteRows: boolean = false;

  /**
   * 批次刪除成功後是否要顯示成功訊息。
   * 只有真的從資料庫刪除的狀況才要設定 true，像是查詢功能上的批次刪除。
   * inline edit 的刪除只是從畫面上移掉，還沒有真的刪除，不需要顯示。
   */
  showBatchDeleteSuccessMessage: boolean = false;

  // ------------ 以下開始為 Table Row Action 相關 properties -------------

  /**
   * 是否要顯示 table 最後方的 Actions 欄位。
   * 預設是 true，要顯示。
   *
   * 如果資料查出來後只有要呈現，什麼都沒有要做，可以 override 改成 false。
   */
  showActions: boolean = true;

  /**
   * Table Row 是否支援刪除資料。
   * 預設是 true。如果是 false，就不會產生刪除的按鈕。
   */
  canDeleteRowData: boolean = true;

  /**
   * Table Row 刪除的按鈕要換成清除。
   * 預設是 false。
   *
   * 注意：這個應該只有不是直接會更新回 DB 的才會設成 true。
   */
  rowDeleteChangeToClear: boolean = false;

  /**
   * Table 資料刪除時要取哪個欄位的值來顯示提示。
   *
   * 如果 canDeleteRowData = false，這個可以實作後設定空白值就可以。
   */
  abstract rowDataDeleteHintFieldName: string;

  /**
   * Table Row Actions 選單。
   */
  rowActionMenu: MenuItem[] = [];

  /**
   * 用在記錄目前 Table Actions 選單選的是哪一筆的資料。
   * 如果希望寫程式可以點出提示，可以 override 這個自行定義資料型別。
   */
  rowCurrentData: any;

  /**
   * 明細 Dialog 的設定。
   */
  dialogConfig?: DialogConfig;

  // ------------ 以下開始為 Table 分頁籤顯示同一筆資料的設定 -------------

  /**
   * Table 分頁頁籤。
   */
  detailTabs: MenuItem[] = [];

  /**
   * Table 分頁頁籤每頁裡面分別放了哪些欄位。
   */
  detailTabColumns: { [tabId: string]: TableColumn[] } = {};

  /**
   * Table 分頁頁籤物件。
   * 如果要啟用 Tab 頁籤，要 override 這個，把 ? 變成 !。
   *
   * 要用 @ViewChild('tabMenu 上 #的名稱') 去畫面取得物件。
   * 如果沒特別調整就是 @ViewChild('detailTabMenuComponent') override detailTabMenuComponent!: TabMenu;
   */
  @ViewChild('detailTabMenuComponent') detailTabMenuComponent?: TabMenu;

  /**
   * Table 分頁頁籤每頁的 Table 分頁、欄寬等狀態。
   */
  detailTabTableStates: { [tabId: string]: TableState } = {};

  /**
   * Table 分頁頁籤前一個點到的 MenuItem。
   * 因為 PrimeNG TabMenu activeItemChange 不是真的改變才會觸發，
   * 一直點同一個 Tab 會一直觸發，用來判斷是不是按到同一個。
   */
  detailTabPreviousActiveItem: MenuItem = {};

  /**
   * Table 分頁頁籤是否有切換到不同的項目上。
   */
  detailTabIsChanged: boolean = false;

  // ------------ 以下開始為 Table Filter 的設定 -------------

  /**
   * 有沒有 filter 功能。
   * 預設是 false，如果有再 override 覆寫成 true。
   */
  hasTableFilter: boolean = false;

  /**
   * 是否開啟篩選列，預設是 false 關閉。
   */
  tableFilterShow: boolean = false;

  // ------------ 以下開始為 Subject -------------
  /**
   * Table 選取的資料筆數改變。
   */
  readonly selectedRowDataCount$ = new Subject<number>();

  /**
   * Table 資料筆數改變。
   */
  readonly rowDataCountChange$ = new Subject<number>();

  protected storageService = inject(StorageService);
  protected dialogService = inject(DialogService);
  protected filterService = inject(FilterService);
  protected renderer = inject(Renderer2);

  /**
   * override BaseFormComponent 的 ngOnInit()。
   * 先呼叫 super 做完 BaseFormComponent 的控制後，
   * 設定 tableStateStorageName。
   */
  override ngOnInit(): void {
    super.ngOnInit();
    console.log('BaseFormTableComponent ngOnInit');
    this.tableStateStorageName = this.componentClassName + 'TableState';

    // 如果 Table 有 Filter 功能才要做
    if (this.hasTableFilter) {
      this.overrideDateFilters();
    }
  }

  /**
   * override PrimeNG FilterService 日期的判斷。
   * 原因是因為 Table 資料日期欄位可能是 string 的型態，原本的程式處理會有問題。
   * 這裡改寫 dateIs、dateIsNot、dateBefore、dateAfter 如果 value 不是 Date 類型，就先轉型才比較。
   */
  private overrideDateFilters(): void {
    // 日期等於
    this.filterService.filters['dateIs'] = (value: any, filter: any): boolean => {
      if (filter === undefined || filter === null) {
        return true;
      }
      if (value === undefined || value === null) {
        return false;
      }
      const parsedDateValue = this.parseDateValue(value);
      return parsedDateValue ? parsedDateValue.toDateString() === filter.toDateString() : false;
    };

    // 日期不等於
    this.filterService.filters['dateIsNot'] = (value: any, filter: any): boolean => {
      if (filter === undefined || filter === null) {
        return true;
      }
      if (value === undefined || value === null) {
        return false;
      }
      const parsedDateValue = this.parseDateValue(value);
      return parsedDateValue ? parsedDateValue.toDateString() !== filter.toDateString() : false;
    };

    // 在某個日期之前
    this.filterService.filters['dateBefore'] = (value: any, filter: any): boolean => {
      if (filter === undefined || filter === null) {
        return true;
      }
      if (value === undefined || value === null) {
        return false;
      }
      const parsedDateValue = this.parseDateValue(value);
      return parsedDateValue ? parsedDateValue.getTime() < filter.getTime() : false;
    };

    // 在某個日期之後
    this.filterService.filters['dateAfter'] = (value: any, filter: any): boolean => {
      if (filter === undefined || filter === null) {
        return true;
      }
      if (value === undefined || value === null) {
        return false;
      }
      const parsedDateValue = this.parseDateValue(value);
      return parsedDateValue ? parsedDateValue.getTime() > filter.getTime() : false;
    }
  }

  /**
   * 取得轉換後的 Date 物件
   * @param originalValue Table 裡資料儲存的值
   * @returns 轉換後的 Date 物件
   */
  private parseDateValue(originalValue: any): Date | undefined {
    // 如果 Table 內的資料是 Date 格式，可以直接回傳去做比較
    if (originalValue instanceof Date) {
      return originalValue;
    } else {
      // 先轉成 Date 物件，如果不能正確轉為日期，就回傳 undefined
      const date = new Date(originalValue);
      if (isNaN(date.getTime())) {
        return undefined;
      } else {
        return date;
      }
    }
  }

  /**
   * 初始化完 Component View 及 Child Component View 之後呼叫。
   * 重置 Table 的狀態。
   */
  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.resetTableState();
  }

  /**
   * 重置 Table 的狀態，包含分頁、排序、選取的資料列等。
   */
  public resetTableState(): void {
    // 停止變更偵測
    this.changeDetectorRef.detach();
    // 清除選取資料
    this.clearRowSelectedDatas();
    // 觸發變更偵測
    this.changeDetectorRef.detectChanges();
    // 重啟變更偵測
    this.changeDetectorRef.reattach();

    if (this.tableComponent) {
      // 清除 session storage 的 Table 狀態
      this.tableComponent.clearState();
      // 還原 Table 狀態
      this.tableComponent.reset();
      // 清除 filter，我看元件 reset() 原始碼裡面有試圖去清除 filterValue，但沒有做完全，或許之後官方會修正就不需要這個了
      this.tableComponent.filters = {};
    }
  }

  /**
   * override BaseFormComponent 的 doFormDataLoadSuccess()。
   * 先呼叫 super 做完原本單檔的控制外，要做下方 Table 和功能選單的控制。
   *
   * (1) 設定 Table 上方的功能選單。
   * (2) 設定 Table 欄位。
   * (3) 設定 Table Row Action 功能選單。
   * (4) 訂閱 Table 選取資料改變的通知。
   */
  override doFormDataLoadSuccess(): void {
    super.doFormDataLoadSuccess();

    // 清除 Table 狀態，避免 Table 勾選的資料保留住造成異常
    this.resetTableState();

    // 設定 Table 上方的功能選單
    this.initDetailMenu();
    this.initExtraDetailMenu();
    this.setExtraDetailMenuStatus();
    this.redrawDetailMenu();

    // 設定 Table 欄位
    this.initTableColumns();
    // 為了避免 initTableColumns 沒有寫，在這多加判斷
    // 如果 selectedColumns 沒有塞值，就直接把 tableColumns 所有欄位都設定進去
    if (this.selectedColumns.length == 0) {
      this.selectedColumns = this.tableColumns;
    }

    // 如果 Table Tab 前一個點到的頁籤是空的，代表是第一次進入，就塞目前 activeItem
    if (this.detailTabs.length > 0 && this.detailTabMenuComponent && !this.detailTabPreviousActiveItem.id) {
      this.detailTabPreviousActiveItem = this.detailTabMenuComponent.activeItem!;
    }

    // 設定 Table Row Action 功能選單
    this.initRowActionMenu();
    this.initExtraRowActionMenu();
    this.setExtraRowActionMenuStatus();
    this.redrawRowActionMenu();

    this.initSelectedRowChangeSubscribe();
    this.initRowDataCountChangeSubscribe();
  }

  /**
   * override BaseFormComponent 的 doFormDataLoadFail()。
   * 先呼叫 super 做完原本單檔的控制外，雙檔資料載入失敗要多做 Table 相關的按鈕不能操作。
   *
   * (1) 所有 Table 上方的功能選單都不能按。
   * (2) 所有 Table Row Action 功能選單都不能按。
   */
  override doFormDataLoadFail(): void {
    super.doFormDataLoadFail();

    // 單頭控制已經在 super 裡做了，這邊做 Table 的控制
    // 如果載入資料失敗，所有按鈕都不能按
    this.detailMenu.forEach(menuItem => {
      menuItem.disabled = true;
    });

    this.rowActionMenu.forEach(menuItem => {
      menuItem.disabled = true;
    });

    this.redrawDetailMenu();
  }

  /**
   * override BaseFormComponent 的 doLangChange()。
   * 先呼叫 super 做完原本單檔的控制外，雙檔語系變更時要多做 Table 資料和選單的變更。
   *
   * (1) 變更 Table 上方的功能選單語系。
   * (2) 變更 Table Row Action 選單語系。
   */
  override doLangChange(): void {
    super.doLangChange();
    // 變更 Table 功能選單語系
    this.languageService.processMenuTranslation(this.detailMenu);
    this.redrawDetailMenu();
    // 變更 Table Row Action 選單語系
    this.languageService.processMenuTranslation(this.rowActionMenu);

    if (this.detailTabs.length > 0 && this.detailTabMenuComponent) {
      // 變更 Table Tab 語系
      this.languageService.processMenuTranslation(this.detailTabs);
      this.detailTabs = [...this.detailTabs];
    }
  }

  /**
     * 單頭功能選單 - Discard 按下去後預設要做的事情
     * override baseForm 是因為要多清空單身的資料。
     *
     * (1) 跳出確認視窗確認是否不保存資料。
     * (2) 執行 beforeMasterMenuDiscard。
     * (3) 單頭狀態切回瀏覽狀態。
     * (4) 清空單頭和單身資料。
     * (5) 如果是新增資料，放棄後直接關閉 Dialog。
     * (6) 如果不是新增資料，顯示處理中遮罩。
     * (7) 呼叫 doLoadFormData() 重新取資料復原畫面。
     */
  override async masterMenuDiscardCommand(): Promise<void> {
    this.systemConfirmationService.confirmDiscardChanges(async() => {
      // 先做 before 事件
      const canDiscard = await this.beforeMasterMenuDiscard();

      if (!canDiscard) {
        return;
      }

      // 單頭狀態切回瀏覽狀態
      this.setMasterMenuStatus(FormStatus.VIEW);

      // 先把欄位都清空
      this.formGroup.reset();

      // 單身資料清空
      this.detailDatas = [];

      // 如果是新增資料，放棄後直接關閉 Dialog
      if (!this.masterDataKeyValue) {
        this.closeDialog();
      } else {
        this.loadingMaskService.show();
        // 重取資料
        this.doLoadFormData();
      }
    });
  }

  /**
   * 需要在選取的資料改變時做的事情。
   * 通常是改變 Table 上方額外的功能選單狀態。
   *
   * 如果沒有特別要做的事情，實作後直接 return 就可以。
   */
  abstract afterSelectedRowDataCountChange(selectedDataCount: number): void;

  /**
   * 需要在資料筆數改變時做的事情。
   * 如果沒有特別要做的事情，實作後直接 return 就可以。
   */
  abstract afterRowDataCountChange(rowDataCount: number): void;

  /**
   * 實作 BaseFormComponent 的 afterMasterMenuStatusChange()。
   *
   * 單頭的狀態改變要一起改變 Table 上方的功能選單狀態。
   */
  override afterMasterMenuStatusChange(): void {
    // 一般的雙檔編輯，單身的狀態與單頭一致，單頭進入編輯後才能編輯單身
    this.detailFormStatus = this.masterFormStatus;
    this.setDetailMenuStatus();
  }

  /**
   * 初始化 Table 上方功能選單。
   * (1) 增加。
   */
  initDetailMenu(): void {
    // automationId 其實設計是用在自動化測試工具可以通過這個識別符號來識別和操作這個元素。
    // 但維運 Team 測試是用 id，為了做到語系變更可以即時切換顯示，所以透過不會用到的 automationId 來設定 i18n 的 Key
    this.detailMenu = [
      {
        id: this.parseToId(MenuItemId.DETAIL_MENU_ADD),
        label: this.translateService.instant(MenuItemId.I18N_ADD),
        automationId: MenuItemId.I18N_ADD,
        icon: 'pi pi-fw pi-plus',
        command: () => {
          this.detailMenuAddCommand();
        }
      }
    ];

    // 是否要顯示 Batch Delete 按鈕
    if (this.canBatchDeleteRows) {
      this.detailMenu.push({
        id: this.parseToId(MenuItemId.DETAIL_MENU_DELETE),
        label: this.rowDeleteChangeToClear ? this.translateService.instant(MenuItemId.I18N_CLEAR) : this.translateService.instant(MenuItemId.I18N_DELETE),
        automationId: this.rowDeleteChangeToClear ? MenuItemId.I18N_CLEAR : MenuItemId.I18N_DELETE,
        icon: this.rowDeleteChangeToClear ? 'pi pi-fw pi-minus' : 'pi pi-fw pi-trash',
        command: () => {
          this.detailMenuBatchDeleteCommand();
        }
      });
    }

    if (this.hasTableFilter) {
      this.detailMenu.push({
        id: this.parseToId(MenuItemId.DETAIL_MENU_FILTER),
        label: this.translateService.instant(MenuItemId.I18N_FILTER),
        automationId: MenuItemId.I18N_FILTER,
        icon: 'pi pi-fw pi-filter',
        command: () => {
          this.detailMenuFilterCommand();
        }
      });
    }

    this.setDetailMenuStatus();
  }

  /**
   * Table 上方的功能選單 - Add 按下去後預設要做的事情。
   * (1) 清除選取的資料列。
   * (2) 初始化 Dialog 設定。
   * (3) 開啟 Dialog。
   */
  detailMenuAddCommand(): void {
    console.log('BaseFormTableComponent detailMenuAddCommand');
    // 清除選取的資料列
    this.clearRowSelectedDatas();
    // 初始化 Dialog 設定
    this.initDialogConfig(DataAction.NEW);
    // 開啟 Dialog
    this.openDialog();
  }

  /**
   * Table 上方的功能選單 - Delete 按下去後預設要做的事情。
   * (1) 跳出確認視窗確認是否要刪除資料。
   * (2) 確認要刪除資料的話，顯示處理中遮罩。
   * (3) 呼叫 doDetailMenuBatchDelete()，處理實際刪除要做的事情。
   * (4) 處理完後呼叫 afterDetailMenuBatchDelete()。
   */
  async detailMenuBatchDeleteCommand(): Promise<void> {
    console.log('BaseFormTableComponent detailMenuBatchDeleteCommand');

    const confirmFunction = this.rowDeleteChangeToClear
      ? this.systemConfirmationService.confirmClearSelected.bind(this.systemConfirmationService)
      : this.systemConfirmationService.confirmDeleteSelected.bind(this.systemConfirmationService);

    const confirmAction = async () => {
      this.loadingMaskService.show();
      const canDelete = await this.beforeDetailMenuBatchDelete();
      if (canDelete) {
        const deleted = await this.doDetailMenuBatchDelete();
        if (deleted) {
          this.afterDetailMenuBatchDelete();
        } else {
          this.loadingMaskService.hide();
        }
      } else {
        this.loadingMaskService.hide();
      }
    };

    confirmFunction(confirmAction, this.rowSelectedDatas.length);
  }

  /**
   * 批次刪除前要先做的事情。
   * 通常是資料的預處理。
   * 本來應該用 abstract 讓各功能實做，但怕影響到既有程式，所以這樣寫
   * 有需要的再 override 後自行實做。
   */
  async beforeDetailMenuBatchDelete(): Promise<boolean> {
    return true;
  }

  /**
   * 要自己實作實際批次刪除的事件。
   */
  abstract doDetailMenuBatchDelete(): Promise<boolean>;

  /**
   * 刪除後要做的事情。
   * (1) 把資料從 table 中移除。
   * (2) 顯示刪除成功訊息。
   * (3) 刪除所有已選擇資料。
   * (4) 移除處理中遮罩。
   */
  afterDetailMenuBatchDelete(): void {
    console.log('BaseFormTableComponent afterDetailMenuBatchDelete');
    // 這種刪除法不會從 DB 重新抓最新的資料，後續看狀況如果有要重抓再改寫法
    this.detailDatas = this.detailDatas.filter(data => {
      return !this.rowSelectedDatas.some((selectedData: { [x: string]: any; }) =>
        selectedData[this.rowDataKeyName] === data[this.rowDataKeyName]);
    });
    this.clearRowSelectedDatas();

    if (this.showBatchDeleteSuccessMessage) {
      this.systemMessageService.success('message.detail.delete-success');
    }

    this.loadingMaskService.hide();

    // 資料刪除後，發送資料筆數改變通知
    this.fireRowDataChanged();
  }

  /**
   * Table 上方的功能選單 - Filter 按下去後預設要做的事情。
   * (1) 開關篩選列。
   * (2) 變更功能選單顯示的字。
   */
  detailMenuFilterCommand(): void {
    // 會進來這裡就是一定有按鈕，所以再做判斷了
    let detailMenuFilter = this.getDetailMenuItem(MenuItemId.DETAIL_MENU_FILTER);

    // 切換隱藏和顯示
    this.tableFilterShow = !this.tableFilterShow;
    if (detailMenuFilter) {
      // 只要有動到篩選的按鈕就清除 Table 選擇的資料
      this.clearRowSelectedDatas();
      // 還原 Table 狀態
      this.tableComponent.reset();
      // 清除 filter，我看元件 reset() 原始碼裡面有試圖去清除 filterValue，但沒有做完全，或許之後官方會修正就不需要這個了
      this.tableComponent.filters = {};

      // 儲存到 storage 裡的選擇和篩選資料也要清除
      const storage = this.tableComponent.getStorage();
      // 因為 reset 會把 Table 排序都清掉，所以要把他從 storage 寫回來
      let sortField;
      let sortOrder;
      if (this.tableComponent.stateKey) {
        let state = storage.getItem(this.tableComponent.stateKey);
        let tableState: TableState;
        if (state) {
          tableState = JSON.parse(state);
          tableState.selection = [];
          tableState.filters = {};
          sortField = tableState.sortField;
          sortOrder = tableState.sortOrder;
          storage.setItem(this.tableComponent.stateKey, JSON.stringify(tableState));
        }
      }

      // 變更篩選按鈕顯示的圖示和文字
      const filterAutomationId = this.tableFilterShow ? MenuItemId.I18N_FILTER_OFF : MenuItemId.I18N_FILTER;
      const filterIcon = this.tableFilterShow ? 'pi pi-fw pi-filter-slash' : 'pi pi-fw pi-filter';
      detailMenuFilter.label = this.translateService.instant(filterAutomationId);
      detailMenuFilter.automationId = filterAutomationId;
      detailMenuFilter.icon = filterIcon;

      // 因為 reset 會把 Table 排序都清掉，所以要把他從 storage 寫回來
      if (sortField && sortOrder) {
        this.tableComponent._sortField = sortField;
        this.tableComponent._sortOrder = sortOrder;
        this.tableComponent.sortSingle();
      }
    }

    this.setDetailMenuStatus();
  }

  /**
   * 單身是否在瀏覽狀態。
   * @returns 單身是否在瀏覽狀態
   */
  detailInViewStatus(): boolean {
    return this.detailFormStatus == FormStatus.VIEW;
  }

  /**
   * 設定 Table 上方功能選單各按鈕的狀態。
   */
  setDetailMenuStatus(): void {
    // 如果單頭有功能選單，才要根據單頭功能選單的操作來控制 Table 上方的功能選單能不能按
    if (this.hasMasterMenuBar) {
      let detailMenuAdd = this.getDetailMenuItem(MenuItemId.DETAIL_MENU_ADD);
      if (detailMenuAdd) {
        // 瀏覽狀態和篩選狀態不能按新增
        detailMenuAdd.disabled = this.detailInViewStatus() || this.tableFilterShow;
        detailMenuAdd.visible = !this.masterInViewStatus();
      }

      let detailMenuFilter = this.getDetailMenuItem(MenuItemId.DETAIL_MENU_FILTER);
      if (detailMenuFilter) {
        detailMenuFilter.disabled = false;
      }
    }

    if (this.canBatchDeleteRows) {
      let detailMenuDelete = this.getDetailMenuItem(MenuItemId.DETAIL_MENU_DELETE);
      if (detailMenuDelete) {
        detailMenuDelete.disabled = this.detailInViewStatus() || (this.rowSelectedDatas && this.rowSelectedDatas.length <= 0);
        detailMenuDelete.visible = !this.masterInViewStatus();
      }
    }

    this.setExtraDetailMenuStatus();
    this.redrawDetailMenu();
    this.afterDetailMenuStatusChange();
  }

  /**
   * 取得指定的 Table 上方功能選單。
   * @param menuItemId 功能選單 id
   * @returns 功能選單
   */
  getDetailMenuItem(menuItemId: string): MenuItem | undefined {
    return this.detailMenu.find(menuItem => menuItem.id == this.parseToId(menuItemId));
  }

  /**
   * 重繪 Table 上方功能選單。
   */
  redrawDetailMenu(): void {
    this.detailMenu = [...this.detailMenu];
  }

  /**
   * 單身功能列是否顯示。
   * 功能列要有按鈕、且要有可見的按鈕才能顯示。
   */
  detailMenubarVisible(): boolean {
    const hasVisibleMenuItem = this.detailMenu.some(menuItem => menuItem.visible !== false);
    return this.detailMenu.length > 0 && hasVisibleMenuItem;
  }

  /**
   * 單身功能選單按了之後會觸發 setDetailMenuStatus()，如果在這之後有要額外做事情可以寫在這裡。
   * 這個通常是為了給 inline edit 用的。
   *
   * 如果沒有特別要做的事情，實作後直接 return 就可以。
   */
  abstract afterDetailMenuStatusChange(): void;

  /**
   * 訂閱 Table 選取資料改變的通知。
   */
  private initSelectedRowChangeSubscribe(): void {
    this.selectedRowDataCount$.pipe(
      takeUntil(this._destroying$)
    ).subscribe(dataCount => {
      this.isRowSelected = dataCount > 0;
      this.setExtraDetailMenuStatus();
      this.setExtraRowActionMenuStatus();
      this.afterSelectedRowDataCountChange(dataCount);
    });
  }

  /**
   * 訂閱 Table 資料長度變動的通知。
   */
  private initRowDataCountChangeSubscribe(): void {
    this.rowDataCountChange$.pipe(
      takeUntil(this._destroying$)
    ).subscribe(dataCount => {
      this.afterRowDataCountChange(dataCount);
    });

    // 資料載入完成後會呼叫 initRowDataCountChangeSubscribe，發送資料筆數改變通知
    this.fireRowDataChanged();
  }

  /**
   * 初始化 Table 上方額外的功能選單。
   *
   * 如果沒有額外的按鈕，實作後直接 return 就可以。
   */
  abstract initExtraDetailMenu(): void;

  /**
   * 設定 Table 上方額外的功能選單按鈕狀態，
   * 或是有特殊條件要改變預設的功能狀態 (ex: 某些條件不能編輯或刪除)。
   * 這個會在以下呼叫：
   * (1) 初始化 Table 上方選單後。
   * (2) 單頭的狀態改變後。
   * (3) 在每次 Table 資料選取改變後呼叫。
   *
   * 如果沒有特別要做的事情，實作後直接 return 就可以。
   */
  abstract setExtraDetailMenuStatus(): void;

  /**
   * 初始化 Table 欄位。
   * (1) Table 欄位。
   * (2) 有分頁籤切換顯示欄位也是定義在這裡面。
   * (3) selectedColumns 也要一併在這裡面設定。
   */
  abstract initTableColumns(): void;

  /**
   * 初始化 Dialog 設定，包含標題、大小、傳入的資料等等。
   * 要根據不同的 DataAction 做設定，因為新增和編輯可能不一樣。
   *
   * 範例 :
   * this.dialogConfig = {
   *   component: XXXXXComponent,
   *   dataAction: DataAction.NEW,
   *   formStatus: FormStatus.New,
   *   header: 'XXXXX',
   *   data: { 要傳入什麼資料 }
   * };
   */
  abstract initDialogConfig(dataAction: DataAction): void;

  /**
   * 初始化 Table Row Action 功能選單。
   * (1) 明細。
   * (2) 編輯。
   * (3) 刪除。
   */
  private initRowActionMenu(): void {
    // automationId 其實設計是用在自動化測試工具可以通過這個識別符號來識別和操作這個元素。
    // 但維運 Team 測試是用 id，為了做到語系變更可以即時切換顯示，所以透過不會用到的 automationId 來設定 i18n 的 Key
    this.rowActionMenu = [
      {
        id: this.parseToId(MenuItemId.ROW_ACTION_MENU_DETAIL),
        label: this.translateService.instant(MenuItemId.I18N_DETAIL),
        automationId: MenuItemId.I18N_DETAIL,
        icon: 'pi pi-fw pi-eye',
        command: () => {
          this.rowActionDetailCommand();
        }
      }
    ];

    // 如果單頭是有 Menubar 控制狀態的才有編輯按鈕
    if (this.hasMasterMenuBar) {
      this.rowActionMenu.push({
        id: this.parseToId(MenuItemId.ROW_ACTION_MENU_EDIT),
        label: this.translateService.instant(MenuItemId.I18N_EDIT),
        automationId: MenuItemId.I18N_EDIT,
        icon: 'pi pi-fw pi-pencil',
        command: () => {
          this.rowActionEditCommand();
        }
      });
    }

    if (this.canDeleteRowData) {
      this.rowActionMenu.push({
        id: this.parseToId(MenuItemId.ROW_ACTION_MENU_DELETE),
        label: this.rowDeleteChangeToClear ? this.translateService.instant(MenuItemId.I18N_CLEAR) : this.translateService.instant(MenuItemId.I18N_DELETE),
        automationId: MenuItemId.I18N_DELETE,
        icon: this.rowDeleteChangeToClear ? 'pi pi-fw pi-minus' : 'pi pi-fw pi-trash',
        command: () => {
          this.rowActionDeleteCommand();
        }
      });
    }

    this.setRowActionMenuStatus();
  }

  /**
   * 設定 Table Row Action 功能選單各按鈕的狀態。
   */
  private setRowActionMenuStatus(): void {
    // 明細按鈕
    let rowActionMenuDetail = this.getRowActionMenuItem(MenuItemId.ROW_ACTION_MENU_DETAIL);
    if (rowActionMenuDetail) {
      if (this.hasMasterMenuBar) {
        // 如果單頭是有 Menubar 控制狀態的，要在瀏覽狀態才看的到 (因為編輯狀態會改成顯示編輯按鈕)
        rowActionMenuDetail.visible = this.detailInViewStatus();
      }
    }

    // 編輯按鈕，在編輯狀態才會出現、才能按
    let rowActionMenuEdit = this.getRowActionMenuItem(MenuItemId.ROW_ACTION_MENU_EDIT);
    if (rowActionMenuEdit) {
      rowActionMenuEdit.visible = !this.detailInViewStatus();
    }

    // 刪除按鈕，在編輯狀態才能按
    let rowActionMenuDelete = this.getRowActionMenuItem(MenuItemId.ROW_ACTION_MENU_DELETE);
    if (rowActionMenuDelete) {
      rowActionMenuDelete.disabled = this.detailInViewStatus();
    }

    this.setExtraRowActionMenuStatus();
    this.redrawRowActionMenu();
  }

  /**
   * 取得指定的 Table Row Action 功能選單。
   * @param menuItemId 功能選單 id
   * @returns 功能選單
   */
  getRowActionMenuItem(menuItemId: string): MenuItem | undefined {
    return this.rowActionMenu.find(menuItem => menuItem.id == this.parseToId(menuItemId));
  }

  /**
   * 重繪 Table Row Action 功能選單。
   */
  redrawRowActionMenu(): void {
    this.rowActionMenu = [...this.rowActionMenu];
  }

  /**
   * 初始化 Table Row Action 額外的功能選單。
   *
   * 如果沒有額外的功能選單，實作後直接 return 就可以。
   */
  abstract initExtraRowActionMenu(): void;

  /**
   * 設定 Table Row Action 額外的功能選單按鈕狀態，
   * 或是有特殊條件要改變預設的功能狀態 (ex: 某些條件不能編輯或刪除)。
   * 這個會在每次 Table 資料選取改變後呼叫。
   *
   * 如果沒有特別要做的事情，實作後直接 return 就可以。
   */
  abstract setExtraRowActionMenuStatus(): void;

  /**
   * Table Row Action 是否顯示。
   * 要有按鈕才能顯示。
   */
  rowActionMenuVisible(): boolean {
    return this.rowActionMenu.length > 0;
  }

  /**
   * Table 資料選單明細按鈕按下去預設要做的事。
   * 開啟詳細資料頁 Dialog。
   */
  rowActionDetailCommand(): void {
    // 初始化 Dialog 設定
    this.initDialogConfig(DataAction.VIEW);
    // 開啟 Dialog
    this.openDialog();
  }

  /**
   * 開啟 Dialog。
   * @returns DynamicDialogRef
   */
  private openDialog(): DynamicDialogRef | undefined {
    if (!this.dialogConfig) {
      return;
    }

    // 開啟 Dialog
    const ref = this.dialogService.open(this.dialogConfig.component, {
      header: this.dialogConfig.header,
      width: this.dialogConfig.width ? this.dialogConfig.width : '99vw',
      height: this.dialogConfig.height ? this.dialogConfig.height : '98vh',
      data: { ...this.dialogConfig.data, ...{ dataAction: this.dialogConfig.dataAction } }
    });

    // 記錄目前 Scrollbar 位置，並把 Body 捲軸移到最上方
    this.setScrollBeforeOpenDialog();

    this.dialogOpened = true;

    // Dialog 關閉後要做的事情
    ref?.onClose.pipe(
      takeUntil(this._destroying$)
    ).subscribe((returnData: any) => {
      this.doRowActionDialogClosed(returnData);
      this.doExtraDetailDialogClose(returnData);
      this.dialogOpened = false;
      this.detailDialogHide();
      this.restoreScroll();
    });

    return ref;
  }

  /**
   * Table 資料選單編輯按鈕按下去預設要做的事。
   * 開啟詳細資料頁 Dialog。
   */
  rowActionEditCommand(): void {
    // 初始化 Dialog 設定
    this.initDialogConfig(DataAction.EDIT);
    // 開啟 Dialog
    this.openDialog();
  }

  /**
   * Dialog 關閉要做的事情。
   * @param returnData Dialog 傳回來的資料
   */
  doRowActionDialogClosed(returnData: DialogReturnData): void {
    console.log('doRowActionDialogClosed = ' + JSON.stringify(returnData));
    if (!returnData) {
      return;
    }

    if (returnData.dialogReturnDataAction == DataAction.DELETE) {
      // 如果是刪除，要把資料從已經查出來的清單內移除，這裡是呼叫跟 Row 直接刪除後同一個 Function，如果未來有不一樣這裡要調整
      this.afterRowActionDelete();
    } else if (returnData.dialogReturnDataAction == DataAction.EDIT) {
      // 如果是編輯，要把後面回傳回來的資料更新到清單上
      // 先找出後面回傳的資料在清單內的 index
      const indexToUpdate = this.detailDatas.findIndex(data => data[this.rowDataKeyName] === returnData.returnData[this.rowDataKeyName]);
      this.detailDatas[indexToUpdate] = returnData.returnData;
      this.detailDatas = [...this.detailDatas];
    } else if (returnData.dialogReturnDataAction == DataAction.NEW) {
      this.changeDetectorRef.detach();  // 停止變更偵測
      this.resetTableState(); // 重置頁數狀態
      // 不能用 unshift 或 push 塞資料，這樣如果先在畫面排序了，資料加上去不會出現在畫面上
      this.detailDatas = [returnData.returnData, ...this.detailDatas]; // 如果是新增，把資料加到清單第一筆

      // 選取第一筆資料
      if (this.tableSelectionMode == TableSelectionMode.SINGLE) {
        this.rowSelectedDatas = this.detailDatas[0];
      } else {
        this.rowSelectedDatas = [this.detailDatas[0]];
      }
      this.selectedRowDataChanged();

      // 發送資料筆數改變通知
      this.fireRowDataChanged();

      this.changeDetectorRef.detectChanges(); // 觸發變更偵測
      this.changeDetectorRef.reattach();  // 重啟變更偵測
    }
  }

  /**
   * 明細 Dialog 關閉後要做的事情。
   *
   * 如果沒有特別要做的事情，實作後直接 return 就可以。
   */
  abstract doExtraDetailDialogClose(returnData: DialogReturnData): void;

  /**
   * Table 資料選單刪除按鈕按下去預設要做的事。
   * (1) 跳出確認視窗確認是否要刪除資料。
   * (2) 確認要刪除資料的話，顯示處理中遮罩。
   * (3) 呼叫 doRowActionDelete()，處理實際刪除要做的事情。
   * (4) 處理完後呼叫 afterRowActionDelete()。
   */
  rowActionDeleteCommand(): void {
    const deleteHint = this.rowDataDeleteHintFieldName ? this.rowCurrentData[this.rowDataDeleteHintFieldName] : '';

    if (this.rowDeleteChangeToClear) {
      this.systemConfirmationService.confirmClearSelected(
        async() => {
          this.loadingMaskService.show();
          const deleted = await this.doRowActionDelete();
          if (deleted) {
            this.afterRowActionDelete();
          }
        },
        deleteHint
      );
    } else {
      this.systemConfirmationService.confirmDelete(
        async() => {
          this.loadingMaskService.show();
          const deleted = await this.doRowActionDelete();
          if (deleted) {
            this.afterRowActionDelete();
          }
        },
        deleteHint
      );
    }
  }

  /**
   * 要自己實作實際刪除的事件。
   */
  abstract doRowActionDelete(): Promise<boolean>;

  /**
   * 刪除後要做的事情。
   * (1) 把資料從 table 中移除。
   * (2) 顯示刪除成功訊息。
   * (3) 刪除所有已選擇資料。
   * (4) 移除處理中遮罩。
   */
  afterRowActionDelete(): void {
    console.log('BaseFormTableComponent afterRowActionDelete');
    // 這種刪除法不會從 DB 重新抓最新的資料，後續看狀況如果有要重抓再改寫法
    this.detailDatas = this.detailDatas.filter(data => {
      return data[this.rowDataKeyName] !== this.rowCurrentData[this.rowDataKeyName]
    });
    this.systemMessageService.success('message.detail.delete-success');
    this.clearRowSelectedDatas();
    this.loadingMaskService.hide();

    // 資料刪除後，發送資料筆數改變通知
    this.fireRowDataChanged();
  }

  /**
   * Table Action 按鈕按下去的時候要把該筆資料記錄下來。
   * @param rowData 點選的資料
   */
  clickRowActionMenu(rowData: any): void {
    // console.log('clickRowActionMenu rowData = ' + JSON.stringify(rowData));
    this.rowCurrentData = rowData;

    if (this.tableSelectionMode == TableSelectionMode.SINGLE) {
      this.rowSelectedDatas = rowData;
    } else {
      this.rowSelectedDatas = [rowData];
    }
    this.selectedRowDataChanged();

    // 變更 MenuItem 的 disabled 狀態 (裡面也會做額外處理的 MenuItem 的 disabled 狀態)
    this.setRowActionMenuStatus();
  }

  /**
   * 改變指定 MenuItem 的 disabled 狀態。
   * @param menuItemId MenuItem 的 id 名稱，不需要 parseToId
   * @param disabled false: 可以按 / true: 不能按，default = false
   */
  setRowActionItemDisable(menuItemId: string,  disabled: boolean = false) {
    let menuItem = this.rowActionMenu.find(menuItem => menuItem.id == this.parseToId(menuItemId));
    if (menuItem) {
        menuItem.disabled = disabled;
    }
  }

  /**
   * Table Row Select 後要做的事，這裡統一觸發 selectedRowDataChanged。
   */
  onRowSelect() : void {
    this.selectedRowDataChanged();
  }

  /**
   * Table Row Unselect 後要做的事，這裡統一觸發 selectedRowDataChanged。
   */
  onRowUnselect() : void {
    this.selectedRowDataChanged();
  }

  /**
   * Table Header 全選的 Checkbox 改變後要做的事，這裡統一觸發 selectedRowDataChanged()。
   */
  onHeaderCheckboxToggle(): void {
    this.selectedRowDataChanged();
  }

  /**
   * _selectedRowDataCount 發出 selectedRowDatas 內容長度改變。
   */
  private selectedRowDataChanged(): void {
    if (this.tableSelectionMode == TableSelectionMode.SINGLE) {
      this.selectedRowDataCount$.next(((this.rowSelectedDatas != null) ? 1 : 0));
    } else {
      this.selectedRowDataCount$.next(this.rowSelectedDatas.length);
    }

    this.setDetailMenuStatus();
  }

  /**
   * 發送資料筆數改變通知。
   */
  fireRowDataChanged(): void {
    this.rowDataCountChange$.next(this.detailDatas.length);
  }

  /**
   * 清除 Table 多選選擇的資料。
   */
  clearRowSelectedDatas(): void {
    if (this.tableSelectionMode == TableSelectionMode.SINGLE) {
      this.rowSelectedDatas = null;
    } else {
      this.rowSelectedDatas = [];
    }

    if (this.tableComponent) {
      // storage 裡的 selection 也要清空
      const storage = this.tableComponent.getStorage();

      if (this.tableComponent.stateKey) {
        let state = storage.getItem(this.tableComponent.stateKey);
        if (state) {
          let tableState: TableState;
          tableState = JSON.parse(state);
          tableState.selection = [];
          storage.setItem(this.tableComponent.stateKey, JSON.stringify(tableState));
        }
      }

      this.tableComponent.selection = undefined;
    }
    this.selectedRowDataChanged();
  }

  /**
   * Table 頁籤切換的事件。
   * 要變更顯示的欄位，並且設定欄位寬度。
   *
   * @param item Newly selected MenuItem
   */
  onDetailTabActiveItemChange(item: MenuItem) {
    // console.log('onDetailTabActiveItemChange item = ' + JSON.stringify(item));
    // console.log('onDetailTabActiveItemChange detailTabPreviousActiveItem = ' + JSON.stringify(this.detailTabPreviousActiveItem));

    this.detailTabIsChanged = item != this.detailTabPreviousActiveItem;
    if (!this.detailTabIsChanged) {
      return;
    }

    // 變更頁籤要顯示哪些欄位
    this.selectedColumns = this.detailTabColumns[item.id!];

    // 清除 Table 拖拉欄寬後設定的寬度 style，因為每頁的寬度應該是獨立的，不是沿用上一頁的設定
    this.tableComponent.destroyStyleElement();

    // 移除原本 Table 的寬度設定
    // 取得 tableComponentRef 的原生元素，並轉型為 HTMLElement
    const tableRef = this.tableComponentRef.nativeElement as HTMLElement;
    // 在 tableRef 中尋找第一個符合 'table.p-datatable-table' 選擇器的元素
    const targetTable = tableRef.querySelector('table.p-datatable-table');
    if (targetTable) {
      // 移除 targetTable 元素的 'width' 和 'min-width' 樣式屬性
      (targetTable as HTMLElement).style.removeProperty('width');
      (targetTable as HTMLElement).style.removeProperty('min-width');
    }

    // 先找出目前儲存在 storage 內的 state (可能是前一個頁籤的，或是沒有)
    // 因為除了 columnWidths 和 tableWidth 以外的狀態都還是要沿用
    const storage = this.tableComponent.getStorage();

    if (this.tableComponent.stateKey) {
      let previousState = storage.getItem(this.tableComponent.stateKey);

      // 清除 Table storage 裡記錄的 state 設定
      this.tableComponent.clearState();

      // 如果切到這個頁籤前是有 state 的，除了 columnWidths 和 tableWidth 以外的狀態都還是要沿用
      if (previousState) {
        // 把 storage 裡記錄的 state 字串轉為 TableState 物件
        let previousTableState: TableState = JSON.parse(previousState);
        // 先把寬度設定清空
        previousTableState.columnWidths = undefined;
        previousTableState.tableWidth = undefined;

        // 去找目前進入的這個頁籤有沒有儲存的 state
        let tabState = this.detailTabTableStates[item.id!];
        if (tabState) {
          // console.log('onActiveItemChange tabState = ' + JSON.stringify(tabState));
          this.adjustFrozenColumn(tabState);

          previousTableState.columnWidths = tabState.columnWidths;
          previousTableState.tableWidth = tabState.tableWidth;
        }

        storage.setItem(this.tableComponent.stateKey, JSON.stringify(previousTableState));

        // 把這個頁籤之前儲存的 state 重新載入
        this.tableComponent.restoreState();
        this.tableComponent.restoreColumnWidths();
      }
    }

    this.detailTabPreviousActiveItem = item;

    this.afterDetailTabActiveItemChange();
  }

  /**
   * Detail Table 頁籤切換後要做的事情。
   */
  abstract afterDetailTabActiveItemChange(): void;

  /**
   * 主要是因為頁籤切換的時候會換顯示欄位，但欄位的寬度記錄不是跟著欄位名稱，
   * 所以欄寬會不正確，用這個把它調整回來
   *
   * @param currentTableState 目前這個頁面之前儲存的 TableState
   */
  adjustFrozenColumn(currentTableState: TableState) {
    // console.log('adjustFrozenColumn before = ' + JSON.stringify(currentTableState));
    if (!currentTableState.columnWidths) {
      return;
    }

    // TODO 這裡沒判斷一般欄位凍結，如果有一般欄位凍結的這要改

    // 將欄寬設定字串轉換為陣列
    let columnWidthsArray = currentTableState.columnWidths.split(',');
    // 計算 Table 寬度要調整多少
    let tableWidthAdjust = 0;

    const leftFrozenColumns = DomHandler.find(this.tableComponent.el.nativeElement, 'tr#bpaas-table-header th.p-frozen-column[alignfrozen="left"]');
    leftFrozenColumns.forEach((frozenColumn, index) => {
      // 第二個參數 10 表示使用十進制解析。由於 parseInt() 遇到非數字字元時會停止解析，因此它會自動忽略字串中的非數字部分
      let frozenColumnWidth = parseInt(frozenColumn.style.width, 10);
      console.log('frozenColumn width = ' + frozenColumnWidth);
      // 判斷儲存的寬度與凍結欄位設定的差多少
      tableWidthAdjust = tableWidthAdjust + frozenColumnWidth - parseInt(columnWidthsArray[index]);
      // 把凍結欄位設定的寬度更新回去
      columnWidthsArray[index] = frozenColumnWidth.toString();
    });

    const rightFrozenColumns = DomHandler.find(this.tableComponent.el.nativeElement, 'tr#bpaas-table-header th.p-frozen-column[alignfrozen="right"]');
    rightFrozenColumns.forEach((frozenColumn, index) => {
      // 第二個參數 10 表示使用十進制解析。由於 parseInt() 遇到非數字字元時會停止解析，因此它會自動忽略字串中的非數字部分
      let frozenColumnWidth = parseInt(frozenColumn.style.width, 10);
      console.log('frozenColumn width = ' + frozenColumnWidth);
      // const frozenColumnIndex = columnWidthsArray.length - 1 - index;
      const frozenColumnIndex = leftFrozenColumns.length + this.selectedColumns.length;
      // 判斷儲存的寬度與凍結欄位設定的差多少
      tableWidthAdjust = tableWidthAdjust + frozenColumnWidth - parseInt(columnWidthsArray[frozenColumnIndex]);
      // 把凍結欄位設定的寬度更新回去
      columnWidthsArray[frozenColumnIndex] = frozenColumnWidth.toString();
    });

    currentTableState.columnWidths = columnWidthsArray.join(',');
    // 稍微 -1 避免因不明因素出現水平捲軸，即使欄寬都沒變的狀態
    currentTableState.tableWidth = currentTableState.tableWidth! + (tableWidthAdjust - 1);

    // console.log('adjustFrozenColumn after = ' + JSON.stringify(currentTableState));
  }

  /**
   * Table 狀態儲存到 storage 後要額外處理的事情。
   * Table 如果有分頁籤顯示，要分別記錄每個頁籤的狀態。
   *
   * @param state T
   */
  onDetailTableStateSave(state: any) {
    console.log('onStateSave = ' + JSON.stringify(state));
    if (this.detailTabs.length > 0 && this.detailTabMenuComponent) {
      const activeItem = this.detailTabMenuComponent.activeItem; // 取得當前活動的頁籤
      if (activeItem) {
        this.detailTabTableStates[activeItem.id!] = state;
        // console.log('onStateSave this.detailTabTableStates = ' + JSON.stringify(this.detailTabTableStates));
      }
    }
  }

  /**
   * Table filter 後要額外做的事情。
   * 因為下拉的篩選不會寫入 state 裡面，導致如果切換 tab 篩選的欄位值會消失。
   * 或許未來 PrimeNG 會解決，解決前只能自己處理。
   *
   * @param event event.filters Filters object having a field as the property key and an object with value, matchMode as the property value.
   */
  onDetailTableFilter(event: any): void {
    const storage = this.tableComponent.getStorage();
    if (this.tableComponent.stateKey) {
      let state = storage.getItem(this.tableComponent.stateKey);
      if (state) {
        let tableState: TableState = JSON.parse(state);
        tableState.filters = event.filters;
        storage.setItem(this.tableComponent.stateKey, JSON.stringify(tableState));
      }
    }
  }

  /**
   * Table 排序後要額外做的事情。
   * 因為 Table 排序後，Table 資料 value 的順序會和 detailDatas 不一致，
   * 導致後續新增的時候造成資料異常無法新增。
   * 所以每次排序後，要把目前排序後的資料順序再更新回 detailDatas。
   *
   * @param event
   */
  onDetailTableSort(event: any): void {
    if (this.tableComponent) {
      this.detailDatas = [...this.tableComponent.value];
    }
  }

  /**
   * PrimeNG Table 欄寬設定後，就無法拉小於 min-width
   * 用這個來處理當任何一個欄位有拉大，就把所有有設定欄寬的欄位 min-width 設為 15px，讓欄位可以拉小
   *
   * @param event
   */
  onColResize(event: { element: HTMLElement, delta: number }): void {
    const elements = this.elementRef.nativeElement.querySelectorAll('.bpaas-cust-width-column');
    elements.forEach((element: HTMLElement) => {
      this.renderer.setStyle(element, 'min-width', '15px');
    });
  }

  override ngOnDestroy(): void {
    // 清除 Table 狀態，避免 Table 勾選的資料保留住造成異常
    this.resetTableState();
    super.ngOnDestroy();
  }
}
